import { createFileRoute, Link } from "@tanstack/react-router";
import { useSuspenseQuery, queryOptions } from "@tanstack/react-query";
import { useState } from "react";
import { SiteHeader } from "@/components/SiteHeader";
import { SiteFooter } from "@/components/SiteFooter";
import { supabase } from "@/integrations/supabase/client";
import { Mail, Phone, MessageCircle, MapPin, Clock, Plane } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { toast } from "sonner";
import { z } from "zod";

const settingsQuery = queryOptions({
  queryKey: ["settings"],
  queryFn: async () => {
    const { data } = await supabase.from("settings").select("*").eq("id", 1).maybeSingle();
    return data;
  },
});

export const Route = createFileRoute("/contact")({
  head: () => ({
    meta: [
      { title: "Contact Creed Car Rental Miami | Call or Text 24/7" },
      { name: "description", content: "Contact Creed Car Rental in Miami. Call or text +1 (305) 520-9765 for luxury car rental quotes, MIA airport delivery & booking — replies within the hour." },
      { property: "og:title", content: "Contact Creed Car Rental Miami | Call or Text 24/7" },
      { property: "og:description", content: "Call or text our Miami team — we reply within the hour, 7 days a week." },
      { property: "og:url", content: "https://creedcarental.com/contact" },
    ],
    links: [{ rel: "canonical", href: "https://creedcarental.com/contact" }],
  }),
  loader: ({ context }) => context.queryClient.ensureQueryData(settingsQuery),
  component: Contact,
  errorComponent: () => <div className="p-10 text-center">Failed to load.</div>,
});

const messageSchema = z.object({
  name: z.string().trim().min(2, "Name is required").max(100),
  email: z.string().trim().email("Valid email required").max(255),
  phone: z.string().trim().max(30).optional().or(z.literal("")),
  topic: z.string().trim().max(60).optional().or(z.literal("")),
  message: z.string().trim().min(5, "Tell us a bit more").max(1000),
});

// Format a US number into "+1 (305) 520-9765"
function formatPhone(raw?: string | null) {
  if (!raw) return "";
  const d = raw.replace(/\D/g, "");
  const ten = d.length === 11 && d.startsWith("1") ? d.slice(1) : d;
  if (ten.length !== 10) return raw;
  return `+1 (${ten.slice(0, 3)}) ${ten.slice(3, 6)}-${ten.slice(6)}`;
}

// Build a clean E.164-ish digit string for wa.me (single leading 1)
function waNumber(raw?: string | null) {
  if (!raw) return "";
  const d = raw.replace(/\D/g, "");
  if (d.length === 10) return `1${d}`;
  if (d.length === 11 && d.startsWith("1")) return d;
  return d;
}

function Contact() {
  const { data: s } = useSuspenseQuery(settingsQuery);
  const phoneDisplay = formatPhone(s?.contact_phone);
  const phoneDigits = s?.contact_phone?.replace(/\D/g, "") ?? "";
  const wa = waNumber(s?.contact_phone);
  const [sending, setSending] = useState(false);
  const [topic, setTopic] = useState<string>("");

  const onSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = new FormData(e.currentTarget);
    const raw = Object.fromEntries(form);
    const parsed = messageSchema.safeParse({ ...raw, topic });
    if (!parsed.success) {
      toast.error(parsed.error.issues[0]?.message || "Please check your inputs");
      return;
    }
    setSending(true);
    try {
      const to = s?.contact_email;
      if (!to) {
        toast.error("Email isn't configured yet. Please call or text us.");
        return;
      }
      const subject = encodeURIComponent(
        `Creed Car Rental inquiry${parsed.data.topic ? ` — ${parsed.data.topic}` : ""} from ${parsed.data.name}`
      );
      const lines = [
        parsed.data.message,
        "",
        "—",
        `Name: ${parsed.data.name}`,
        `Email: ${parsed.data.email}`,
        parsed.data.phone ? `Phone: ${parsed.data.phone}` : "",
        parsed.data.topic ? `Topic: ${parsed.data.topic}` : "",
      ].filter(Boolean);
      window.location.href = `mailto:${to}?subject=${subject}&body=${encodeURIComponent(lines.join("\n"))}`;
      toast.success("Opening your email client…");
      (e.target as HTMLFormElement).reset();
      setTopic("");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="min-h-screen bg-cream">
      <SiteHeader />

      {/* Hero header */}
      <section className="relative isolate overflow-hidden border-b border-border/60 bg-espresso py-20 md:py-24">
        <div
          className="pointer-events-none absolute inset-0 opacity-60"
          style={{
            background:
              "radial-gradient(60% 80% at 85% 20%, oklch(0.72 0.15 65 / 0.35), transparent 60%), radial-gradient(50% 70% at 10% 90%, oklch(0.72 0.15 65 / 0.18), transparent 60%)",
          }}
          aria-hidden
        />
        <svg
          className="pointer-events-none absolute -right-24 -top-24 h-[520px] w-[520px] text-accent/20"
          viewBox="0 0 600 600"
          fill="none"
          aria-hidden
        >
          <circle cx="300" cy="300" r="220" stroke="currentColor" strokeWidth="1" />
          <circle cx="300" cy="300" r="160" stroke="currentColor" strokeWidth="1" />
          <circle cx="300" cy="300" r="100" stroke="currentColor" strokeWidth="1" />
        </svg>
        <div className="relative mx-auto max-w-7xl px-6">
          <div className="text-xs uppercase tracking-[0.3em] text-accent">Contact</div>
          <h1 className="mt-4 font-display text-5xl text-cream md:text-6xl">Talk to a Creed concierge.</h1>
          <p className="mt-4 max-w-xl text-cream/70">
            Booking a car, planning a delivery, or have a question? Call or text us anytime — we usually reply within the hour, 7 days a week.
          </p>
        </div>
      </section>

      <section className="mx-auto max-w-5xl px-6 py-20">
        <p className="mt-2 text-sm font-medium text-accent italic">
          No call centers. Direct access to your concierge.
        </p>


        <div className="mt-10 grid gap-10 lg:grid-cols-5">
          {/* Quick contact */}
          <div className="lg:col-span-2 space-y-3">
            {s?.contact_phone && (
              <a
                href={`tel:${phoneDigits}`}
                className="flex items-center gap-3 rounded-lg bg-primary p-5 text-primary-foreground shadow-sm transition hover:opacity-90"
              >
                <Phone className="h-5 w-5" />
                <div>
                  <div className="text-xs uppercase tracking-wider opacity-80">Call now</div>
                  <div className="text-lg font-medium">{phoneDisplay}</div>
                </div>
              </a>
            )}
            {phoneDigits && (
              <a
                href={`sms:+${wa}`}
                className="flex items-center gap-3 rounded-lg border border-border bg-card p-5 hover:border-accent"
              >
                <MessageCircle className="h-5 w-5 text-accent" />
                <div>
                  <div className="text-xs text-muted-foreground">Text / SMS</div>
                  <div className="text-espresso">{phoneDisplay}</div>
                </div>
              </a>
            )}
            {wa && (
              <a
                href={`https://wa.me/${wa}`}
                target="_blank"
                rel="noreferrer"
                className="flex items-center gap-3 rounded-lg border border-border bg-card p-5 hover:border-accent"
              >
                <MessageCircle className="h-5 w-5 text-accent" />
                <div>
                  <div className="text-xs text-muted-foreground">WhatsApp</div>
                  <div className="text-espresso">Message on WhatsApp</div>
                </div>
              </a>
            )}
            {s?.contact_email && (
              <a
                href={`mailto:${s.contact_email}`}
                className="flex items-center gap-3 rounded-lg border border-border bg-card p-5 hover:border-accent"
              >
                <Mail className="h-5 w-5 text-accent" />
                <div>
                  <div className="text-xs text-muted-foreground">Email</div>
                  <div className="text-espresso break-all">{s.contact_email}</div>
                </div>
              </a>
            )}

            <div className="rounded-lg border border-border bg-card p-5 space-y-3 text-sm">
              <div className="flex items-start gap-2 text-espresso">
                <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-accent" />
                <div>
                  <div className="font-medium">Service area</div>
                  <div className="text-espresso/80">
                    Miami Beach · Brickell · Wynwood · Downtown · Aventura ·
                    Coral Gables · Fort Lauderdale
                  </div>
                </div>
              </div>
              <div className="flex items-start gap-2 text-espresso">
                <Plane className="mt-0.5 h-4 w-4 shrink-0 text-accent" />
                <div>
                  <div className="font-medium">Airport delivery</div>
                  <div className="text-espresso/80">MIA &amp; FLL — meet &amp; greet available</div>
                </div>
              </div>
              <div className="flex items-start gap-2 text-espresso">
                <Clock className="mt-0.5 h-4 w-4 shrink-0 text-accent" />
                <div>
                  <div className="font-medium">Hours</div>
                  <div className="text-espresso/80 whitespace-pre-line">
                    {s?.business_hours || "7 days a week · 8am – 10pm ET"}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Message form */}
          <div className="lg:col-span-3 space-y-6">
            <form
              onSubmit={onSubmit}
              className="space-y-4 rounded-lg border border-border bg-card p-6"
            >
              <div>
                <div className="font-display text-xl text-espresso">Prefer to write?</div>
                <p className="text-sm text-muted-foreground">
                  Send a note and we'll follow up shortly.
                </p>
              </div>

              <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-1.5">
                  <Label htmlFor="c-name">Your name</Label>
                  <Input id="c-name" name="name" required maxLength={100} />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="c-email">Email</Label>
                  <Input id="c-email" type="email" name="email" required maxLength={255} />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="c-phone">Phone (optional)</Label>
                  <Input id="c-phone" type="tel" name="phone" maxLength={30} placeholder="+1 (305) 555-0123" />
                </div>
                <div className="space-y-1.5">
                  <Label>What are you interested in?</Label>
                  <Select value={topic} onValueChange={setTopic}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select a topic" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Specific car">Specific car</SelectItem>
                      <SelectItem value="Delivery quote">Delivery quote</SelectItem>
                      <SelectItem value="Long-term rental">Long-term rental</SelectItem>
                      <SelectItem value="Other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="c-message">Message</Label>
                <Textarea
                  id="c-message"
                  name="message"
                  required
                  maxLength={1000}
                  rows={5}
                  placeholder="Which car, what dates, and where would you like delivery?"
                />
              </div>
              <Button type="submit" disabled={sending} className="w-full bg-primary hover:bg-primary/90">
                {sending ? "Sending…" : "Send message"}
              </Button>
              <p className="text-xs text-muted-foreground">
                We reply within the hour, 7 days a week. For instant answers, call or text.
              </p>
            </form>

            {/* FAQ */}
            <div className="rounded-lg border border-border bg-card p-6">
              <div className="font-display text-xl text-espresso">Quick answers</div>
              <Accordion type="single" collapsible className="mt-2">
                <AccordionItem value="age">
                  <AccordionTrigger>How old do I need to be to rent?</AccordionTrigger>
                  <AccordionContent>
                    Drivers must be 25 or older with a valid license and full-coverage insurance.
                    Some exotic vehicles require a minimum age of 25.
                  </AccordionContent>
                </AccordionItem>
                <AccordionItem value="deposit">
                  <AccordionTrigger>Is there a deposit?</AccordionTrigger>
                  <AccordionContent>
                    Yes — a refundable security deposit is held (not charged) on your card at pickup
                    and released after the vehicle is returned in the same condition.
                  </AccordionContent>
                </AccordionItem>
                <AccordionItem value="delivery">
                  <AccordionTrigger>Do you deliver?</AccordionTrigger>
                  <AccordionContent>
                    Yes. We deliver throughout Miami-Dade and Broward, including MIA and FLL
                    airports, hotels, and private residences. Text us your address for an instant quote.
                  </AccordionContent>
                </AccordionItem>
                <AccordionItem value="insurance">
                  <AccordionTrigger>What about insurance?</AccordionTrigger>
                  <AccordionContent>
                    Renters must provide proof of full-coverage auto insurance that extends to rental
                    vehicles. We can recommend short-term coverage if you don't have a policy.
                  </AccordionContent>
                </AccordionItem>
              </Accordion>
              <div className="mt-4 text-sm">
                <Link to="/faq" className="text-accent underline underline-offset-4 hover:text-accent/80">
                  See the full FAQ →
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>
      <SiteFooter />
    </div>
  );
}
